

import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { format } from "date-fns";

import { Botao, Cores, Corpo, Icone } from "@componentes-mu/componentes-mu";

import { Column } from "../../../components/Table/types";
import { PriorityTicketResponse } from "../../../types/tickets/types";
import Table from "../../../components/Table";
import { FlexRow } from "../../../components/Flex";
import { reorderByPrioritySmart } from "./reorderByPrioritySmart";

export enum PriorityTicketTabId {
    ATENDIMENTOS = "atendimentos",
    DESENVOLVIMENTO = "desenvolvimento",
    ORDEM_SERVICO = "ordem-servico",
}

export type ModalPriorityProps = {
    data: PriorityTicketResponse[];
    isDragging: boolean;
    onClose: () => void;
    onConfirm: (tickets: PriorityTicketResponse[]) => void;
    handleOption: (isDragging: boolean) => void;
}

export const ModalPriority = ({ data, onClose, onConfirm, handleOption, isDragging }: ModalPriorityProps) => {

    const [rows, setRows] = useState<PriorityTicketResponse[]>(data);
    const [editingPriority, setEditingPriority] = useState<Record<number, string>>({});
    const dragIndexRef = useRef<number | null>(null);

    useEffect(() => {
        setRows(data);
    }, [data]);


    const hasChanges = useMemo(() => {

        const originalMap = new Map(
            data.map((item) => [item.ticket, item.prioridadeCliente])
        );

        return rows.some(
            (row) =>
                originalMap.get(row.ticket) !== row.prioridadeCliente
        );
    }, [data, rows]);


    function handleDragStart(index: number) {
        dragIndexRef.current = index;
    }

    function handleDrop(dropIndex: number) {
        const dragIndex = dragIndexRef.current;
        if (dragIndex === null || dragIndex === dropIndex) return;

        const updated = [...rows];
        const [removed] = updated.splice(dragIndex, 1);
        updated.splice(dropIndex, 0, removed);

        const withUpdatedPriority = updated.map((item, index) => ({
            ...item,
            prioridadeCliente: index + 1,
        }));

        setRows(withUpdatedPriority);
        dragIndexRef.current = null;
    }


    const handlePriorityChange = useCallback(
        (ticket: PriorityTicketResponse["ticket"], value: number) => {
            setRows((prev) => {
                const current = prev.find((r) => r.ticket === ticket);

                if (!current) return prev;

                const max = prev.length;
                const nextPriority = Math.min(Math.max(value, 1), max);


                if (current.prioridadeCliente === nextPriority) {
                    return prev;
                }

                return reorderByPrioritySmart(
                    prev,
                    item => item.ticket === ticket,
                    value
                );
            });
        },
        []
    );

    const renderContainer = useCallback((conatiner: string, original: PriorityTicketResponse, rowIndex?: number) => {
        if (rowIndex === undefined) return null;
        const conatinerObj: Record<string, ReactElement> = {
            prioridadeCliente: (
                <>
                    {isDragging ?
                        <div
                            draggable
                            onDragStart={() => handleDragStart(rowIndex)}
                            style={{ cursor: "grab" }}
                        >
                            <Icone
                                nome="drag_indicator"
                                cor={Cores.CoresIdentidadeMU.azulMu}
                                altura="18px"
                                largura="18px"
                            />
                        </div>

                        :
                        <input
                            type="number"
                            min={1}
                            max={rows.length}
                            value={
                                editingPriority[original.ticket] ??
                                original.prioridadeCliente ??
                                ""
                            }
                            onChange={(e) =>
                                setEditingPriority((prev) => ({
                                    ...prev,
                                    [original.ticket]: e.target.value,
                                }))
                            }
                            onBlur={(e) => {
                                handlePriorityChange(
                                    original.ticket,
                                    Number(e.target.value)
                                );

                                setEditingPriority((prev) => {
                                    const copy = { ...prev };
                                    delete copy[original.ticket];
                                    return copy;
                                });
                            }}
                            style={{ width: "60px", textAlign: "center" }}
                        />
                    }
                </>
            ),
            inclusao: (
                <>{format(new Date(original.inclusao), 'dd/MM/yyyy')}</>
            ),
            titulo: (
                <div>
                    <span
                        style={{ whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{ __html: original.titulo }}
                    />
                </div>
            ),
        }

        return conatinerObj[conatiner]
    }, [isDragging, rows.length, editingPriority, handlePriorityChange]);

    const columns: Column<PriorityTicketResponse>[] = [
        {
            key: "prioridadeCliente",
            header: "PRIORIDADE",
            width: '10%',
            align: "center",
            Cell: (row, rowIndex) => renderContainer('prioridadeCliente', row, rowIndex),
        },
        {
            key: "ticket",
            header: "Nº TICKET",
            width: '10%',
            align: "center",
        },
        {
            key: "titulo",
            header: "TÍTULO",
            width: '40%',
            align: "center",
            Cell: (row, rowIndex) => renderContainer('titulo', row, rowIndex),
        },
        {
            key: "inclusao",
            header: "INCLUSÂO",
            width: '10%',
            align: "center",
            Cell: (row, rowIndex) => renderContainer('inclusao', row, rowIndex),
        },
        {
            key: "tipo",
            header: "TIPO",
            width: '10%',
            align: "center",
        },
        {
            key: "solicitante",
            header: "SOLICITANTE",
            width: '10%',
            align: "center",
        },
    ]


    return (

        <>
            <FlexRow justifyContent="center" gap="1rem" >
                <button
                    role="tab"
                    style={{
                        padding: '20px 16px',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'transparent',
                        outline: 'none',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Corpo
                        tipo={"label1"}
                        negrito={700}
                        cor={Cores.GradacaoNeutros.PretoMU900}
                        style={{ cursor: 'inherit', alignContent: "center" }}
                    >
                        ATENDIMENTOS
                    </Corpo>
                </button>
            </FlexRow>

            <div
                style={{
                    background: Cores.GradacaoPrimaria.AzulFillMU100,
                    padding: "0.75rem 2rem",
                    borderTop: `1px solid ${Cores.GradacaoNeutros.CinzaFillMU200}`,
                    borderBottom: `1px solid ${Cores.GradacaoNeutros.CinzaFillMU200}`,
                }}
            >
                <FlexRow
                    justifyContent="flex-start"
                    alignItems="center"
                    gap="1.5rem"
                >
                    <Corpo
                        tipo="label2"
                        negrito={500}
                        cor={Cores.GradacaoNeutros.PretoMU900}
                    >
                        Ordenação por:
                    </Corpo>

                    <div
                        style={{
                            display: "flex",
                            borderRadius: "8px",
                            background: Cores.CoresIdentidadeMU.brancoMu,
                            padding: "4px",
                            gap: "4px",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                        }}
                    >
                        <Botao
                            onClick={() => handleOption(false)}
                            style={{
                                padding: "6px 16px",
                                borderRadius: "6px",
                                fontWeight: 500,
                                boxShadow: "none"
                            }}
                            cor={
                                !isDragging
                                    ? Cores.GradacaoPrimaria.AzulFill700
                                    : Cores.CoresIdentidadeMU.brancoMu
                            }
                            corTexto={
                                !isDragging
                                    ? Cores.CoresIdentidadeMU.brancoMu
                                    : Cores.TextoPadrao.CinzaMU600
                            }
                        >
                            Numeração
                        </Botao>

                        <Botao
                            onClick={() => handleOption(true)}
                            style={{
                                padding: "6px 16px",
                                borderRadius: "6px",
                                fontWeight: 500,
                                boxShadow: "none"
                            }}
                            cor={
                                isDragging
                                    ? Cores.GradacaoPrimaria.AzulFill700
                                    : Cores.CoresIdentidadeMU.brancoMu
                            }
                            corTexto={
                                isDragging
                                    ? Cores.CoresIdentidadeMU.brancoMu
                                    : Cores.TextoPadrao.CinzaMU600
                            }
                        >
                            Arraste
                        </Botao>
                    </div>

                    <FlexRow
                        alignItems="center"
                        justifyContent="flex-start"
                        gap="0.35rem"
                        style={{
                            marginLeft: "1rem",
                            flex: 1
                        }}
                    >
                        <Icone
                            nome="info"
                            cor={Cores.GradacaoPrimaria.AzulFill700}
                        />

                        <Corpo
                            tipo="label3"
                            cor={Cores.GradacaoPrimaria.AzulFill700}
                            style={{
                                fontSize: "12px",
                                lineHeight: "16px",
                                margin: 0
                            }}
                        >
                            {isDragging ? (
                                <>
                                    Selecione e arraste o ícone
                                    <Icone
                                        nome="drag_indicator"
                                        cor={Cores.CoresIdentidadeMU.azulMu}
                                        altura="16px"
                                        largura="16px"
                                    />
                                    para reordenar a prioridade dos tickets em aberto
                                </>
                            ) : (
                                "Edite o número nos campos da coluna prioridade para reordenar os tickets em aberto"
                            )}
                        </Corpo>
                    </FlexRow>
                </FlexRow>
            </div>


            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: "70vh",
                    height: "auto",
                }}
            >
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                    }}
                >
                    <Table
                        columns={columns}
                        data={rows}
                        showFooter={false}
                        isDraggingEnabled={isDragging}
                        onRowRender={(rowIndex) => ({
                            onDragOver: (e) => e.preventDefault(),
                            onDrop: () => handleDrop(rowIndex),
                        })}
                    />
                </div>


                <div
                    style={{
                        position: "sticky",
                        bottom: 0,
                        background: "#fff",
                        padding: "1rem",
                        borderTop: "1px solid #e0e0e0",
                        zIndex: 10,
                    }}
                >
                    <FlexRow gap="1rem" justifyContent="flex-end">

                        <Botao onClick={onClose} cor={Cores.CoresIdentidadeMU.azulMu}>
                            Cancelar
                        </Botao>


                        <Botao
                            onClick={() => onConfirm(rows.filter(r => r.prioridadeCliente != null))}
                            cor={hasChanges ? Cores.CoresIdentidadeMU.azulMu : Cores.CoresIdentidadeMU.cinzaMu}
                            desabilitado={!hasChanges}
                        >
                            Confirmar
                        </Botao>
                    </FlexRow>

                </div>

            </div>
        </>
    )
}